import gql from 'graphql-tag';
import React, { useEffect, useRef, useState } from 'react';
import { FunctionComponent } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SmallLoaderRing } from '../../common/components/loaders/SmallLoaderRing';
import { getColorFromCode, MOBILE_SIZE } from '../../common/constants';
import {
  GetCompanyInformation_companyInformation,
  GetFullCompanyInformationVariables,
  GetFullCompanyInformation_fullCompanyInformation,
  validateSiretNumber,
  validateSiretNumberVariables
} from '../../common/graphql';
import { useGetLogin } from '../../hooks/useLogin';
import { VALIDATE_SIRET_NUMBER_MUTATION } from '../../templates/registration/RegistrationSuccess';
import { useIsWindowWidthBelow } from '../../hooks/useWindowSize';
const valid = require('../../../assets/icons/checked.svg');

interface SiretInputProps {
  label: string;
  inputType: string;
  parentErrorMessage?: string | undefined;
  updateFieldsCallback: (line: GetFullCompanyInformation_fullCompanyInformation | null) => void;
}

const dqeLocales: Record<string, string> = {
  fr: 'FRA',
  es: 'ESP'
};

const QUERY_GET_COMPANY_INFORMATION = gql`
  query GetCompanyInformation($queryString: String!, $locale: String!) {
    companyInformation(queryString: $queryString, locale: $locale) {
      companyName
      siret
      companyAddress
      city
      postalCode
      reference
    }
  }
`;

const QUERY_GET_FULL_COMPANY_INFORMATION = gql`
  query GetFullCompanyInformation($siret: String, $reference: String, $locale: String!) {
    fullCompanyInformation(siret: $siret, reference: $reference, locale: $locale) {
      companyName
      streetNumber
      repetitionIndex
      streetType
      streetLabel
      postalCode
      companyPrincipalActivity
      nj
      njLabel
      principalActivityLabel
      staffCount
      staffCountLabel
      creationDate
      caSlice
      updateOrigin
      updateIndex1
      updateIndex2
      updateIndex3
      updateDate
      exploitationName
      siren
      siret
      tvaIntra
      companyDimension
      nafCode
      city
      country
      region
      seasonalActivity
      acronym
      headquarters
      address2
    }
  }
`;

export const SiretInputShared: FunctionComponent<SiretInputProps> = ({ label, inputType, parentErrorMessage, updateFieldsCallback }) => {
  const { t } = useTranslation('sharedComponents');
  const userData = useGetLogin();
  const siret = userData.data?.clientContactFull.Siret;
  const locale = process.env.GATSBY_LOCALE;
  const dqeLocale = (locale && dqeLocales[locale]) || '';
  const errorMessages = {
    COMPANY_NOT_FOUND: t('shared.siretInput.errorMessages.companyNotFound'),
    INCORRECT_SIRET_NUMBER: t('shared.siretInput.errorMessages.incorrectSiretNumber'),
    ALREADY_USED_SIRET_NUMBER: t('shared.siretInput.errorMessages.alreadyUsedSiretNumber')
  };

  const [getCompanyInfo, { loading }] = useLazyQuery(QUERY_GET_COMPANY_INFORMATION, {
    onCompleted: data => {
      if (data.companyInformation.length) {
        setDisplayList(true);
        setCompanyInfo(data.companyInformation);
        setErrorMessage('');
      } else {
        setDisplayList(false);
        if (input) setErrorMessage(errorMessages.COMPANY_NOT_FOUND);
        updateFieldsCallback(null);
      }
    },
    fetchPolicy: 'network-only'
  });

  const inputRef = useRef<HTMLInputElement>();
  const [getFullCompanyInfo] = useLazyQuery(QUERY_GET_FULL_COMPANY_INFORMATION, {
    onCompleted: (data: any) => {
      updateFieldsCallback(data?.fullCompanyInformation);
      if (inputRef && inputRef.current) {
        inputRef.current.value = data.fullCompanyInformation.siret;
      }
    },
    fetchPolicy: 'network-only'
  });

  const [validateSiretNumber] = useMutation<validateSiretNumber, validateSiretNumberVariables>(VALIDATE_SIRET_NUMBER_MUTATION);

  const [companyInfo, setCompanyInfo] = useState<GetCompanyInformation_companyInformation[] | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [input, setInput] = useState('');
  const [displayList, setDisplayList] = useState(false);
  const [checkIconStatus, setCheckIconStatus] = useState(false);
  const isTypeNumber = inputType === 'number';

  const updateFields = (reference: string) => {
    const siretNumberRegex = new RegExp('^([0-9]{14})$');
    let variables: GetFullCompanyInformationVariables = { locale: dqeLocale };
    siretNumberRegex.test(input) ? (variables.siret = input) : (variables.reference = reference);
    getFullCompanyInfo({ variables });
    setDisplayList(false);
    setCheckIconStatus(true);
    setTimeout(() => setCheckIconStatus(false), 2000);
  };

  useEffect(() => {
    if (isTypeNumber) {
      if (input.length === 14 || input.length === 9) {
        (async (input: string) => {
          const validateSiretNumberResponse = await validateSiretNumber({ variables: { siretNumber: input } });
          if (!(input === siret) && validateSiretNumberResponse.data && !validateSiretNumberResponse.data.validateSiretNumber) {
            setErrorMessage(errorMessages.ALREADY_USED_SIRET_NUMBER);
            setCompanyInfo(null);
            setDisplayList(false);
            updateFieldsCallback(null);
          } else {
            getCompanyInfo({ variables: { queryString: input, locale: dqeLocale } });
          }
        })(input);
      } else {
        setErrorMessage('');
        setDisplayList(false);
        updateFieldsCallback(null);
      }
    } else {
      input && getCompanyInfo({ variables: { queryString: input, locale: dqeLocale } });
    }
  }, [input]);

  const onFocus = () => {
    companyInfo && isTypeNumber && setDisplayList(true);
  };

  const onBlur = () => {
    setTimeout(() => setDisplayList(false), 100);
  };

  const updateSiret = (siret: string) => {
    updateFieldsCallback({ siret: siret } as GetFullCompanyInformation_fullCompanyInformation);
  };

  const isMobile = useIsWindowWidthBelow(MOBILE_SIZE);

  return (
    <SiretContainer isMobile={isMobile}>
      <SiretLabel>{label}</SiretLabel>
      <SiretInputContainer>
        {locale === 'es' ? (
          <SiretBasicInput
            type="text"
            id="siretSharedInput"
            maxLength="9"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSiret(e.target.value)}
          ></SiretBasicInput>
        ) : (
          <SiretInput
            ref={inputRef}
            onFocus={onFocus}
            onBlur={onBlur}
            type={inputType}
            error={errorMessage}
            id="siretSharedInput"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
          />
        )}
        <SiretLoaderContainer className={loading ? 'enabled' : 'disabled'}>
          <SmallLoaderRing loadingColor={getColorFromCode('exa pink', '#757575')} />
        </SiretLoaderContainer>
        <SiretValid className={checkIconStatus ? 'enabled' : 'disabled'} />
      </SiretInputContainer>
      <SiretList displayList={displayList}>
        {companyInfo &&
          companyInfo.map((value: GetCompanyInformation_companyInformation, key: number) => {
            return (
              <SiretEntry
                key={key}
                onMouseDown={() => {
                  updateFields(value.reference);
                }}
              >
                <SiretEntryFirstLine>
                  <SiretEntryCompanyName>{value.companyName}</SiretEntryCompanyName>
                  <div>
                    ({value.postalCode} {value.city})
                  </div>
                </SiretEntryFirstLine>
                <SiretEntryAddress>{value.companyAddress}</SiretEntryAddress>
              </SiretEntry>
            );
          })}
      </SiretList>
      {(errorMessage || parentErrorMessage) && <SiretError>{errorMessage || parentErrorMessage}</SiretError>}
    </SiretContainer>
  );
};

const SiretContainer = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: ${({ isMobile }) => (isMobile ? '95%' : '80%')};
  padding: 0 5% 2%;
`;
const SiretLabel = styled.label`
  line-height: 20px;
  font-size: 14px;
`;
const SiretInputContainer = styled.div`
  position: relative;
`;

const SiretInput = styled.input`
  outline: none;
  font-size: 16px;
  padding: 5px 20px 5px 5px;
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${props => (props.error ? getColorFromCode('exa pink', '#757575') : getColorFromCode('exa black', '#757575'))};
  background-color: #fff;
  height: 33px;
  box-sizing: border-box;
  margin-top: 3px;
  & :disabled {
    cursor: not-allowed;
    background-color: #d2d2d2;
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  -moz-appearance: textfield;
`;

const SiretBasicInput = styled.input`
  outline: none;
  font-size: 16px;
  padding: 5px 20px 5px 5px;
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${props => (props.error ? getColorFromCode('exa pink', '#757575') : getColorFromCode('exa black', '#757575'))};
  background-color: #fff;
  height: 33px;
  box-sizing: border-box;
  margin-top: 3px;
  & :disabled {
    cursor: not-allowed;
    background-color: #d2d2d2;
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  -moz-appearance: textfield;
`;

const SiretLoaderContainer = styled.span`
  position: absolute;
  right: 0;
  top: 30%;
  opacity: 0;
  transition: 0.3s ease-in-out;
  &.enabled {
    opacity: 1;
  }
`;
const SiretValid = styled.span`
  width: 16px;
  height: 16px;
  position: absolute;
  right: 0;
  top: 30%;
  background-color: ${getColorFromCode('exa pink', '#757575')};
  mask-image: url(${valid});
  opacity: 0;
  transition: 0.2s ease-in-out;
  &.enabled {
    opacity: 1;
  }
`;
const SiretList = styled.ul`
  overflow: auto;
  min-height: fit-content;
  max-height: 400px;
  position: absolute;
  border-right: 1px solid rgb(50, 50, 50);
  border-bottom: 1px solid rgb(50, 50, 50);
  border-left: 1px solid rgb(50, 50, 50);
  border-image: none 100% / 1 / 0 stretch;
  border-top: unset;
  font-size: 69%;
  z-index: 10;
  width: 80%;
  display: ${props => (props.displayList ? 'block' : 'none')};
`;
const SiretEntry = styled.li`
  width: 100%;
  background-color: rgb(255, 255, 255);
  cursor: pointer;
  &:hover {
    background-color: ${getColorFromCode('exa grey', '#33d1ff')};
  }
  &:first-child {
    padding-top: 5px;
  }
`;
const SiretEntryFirstLine = styled.div`
  padding: 6px 9px 0;
  display: flex;
`;
const SiretEntryCompanyName = styled.div`
  font-weight: bold;
  margin-right: 2px;
`;
const SiretEntryAddress = styled.div`
  color: rgb(255, 61, 119);
  text-transform: lowercase;
  font-size: 11px;
  padding: 0 9px 6px;
`;
const SiretError = styled.div`
  padding: 2px;
  color: ${getColorFromCode('exa pink', '#ff8241')};
`;
